import { useState } from "react";
import shortid from "shortid";
import {
  Grid,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  DatePicker,
  DataGrid,
  Loading,
} from "../../../../../components";
import { ProdutosService, notification } from "../../../../../services";
import { InputErros, value } from "../../../../../helpers";
import { formatPrice } from "../../../../../utils";

const CondicionalStepperContent = ({
  sendServer,
  activeStep,
  setActiveStep,
  handleImportarCondicional,
  handleCancelarImportacao,
  handleDateChange,
  filtrosDialog,
  setFiltrosDialog,
  colunas,
  condicionais,
  setCondicionaisSelecionados,
  itensSelecionados,
  setItensSelecionados,
  buscarCondicionaisAvancado,
  listaClientes,
  handleInputChange,
  itemSelectionModel,
  setItemSelectionModel,
  condicionaisSelectionModel,
  setCondicionaisSelectionModel,
  listaOperacoesFiscais,
  operacaoFiscal,
  setOperacaoFiscal,
}) => {
  const produtosService = new ProdutosService();
  const [loading, setLoading] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const [itensQuantidade, setItensQuantidade] = useState([]);

  const colunasItens = [
    {
      field: "produto",
      headerName: "Produto",
      flex: 500,
    },
    {
      field: "valorUnitario",
      headerName: "Valor (R$)",
      flex: 300,
      valueGetter: (params) => formatPrice(parseFloat(params.value)),
    },
    {
      field: "saldoExportar",
      headerName: "Qtd.",
      flex: 200,
      headerAlign: "start",
      align: "start",
      type: "number",
      editable: true,
      valueParser: (newValue, params) => {
        const maxValue = itensQuantidade?.length
          ? itensQuantidade.find((item) => item.id === params.row?.id)
              ?.saldoExportar
          : params.value;
        const parsedValue = parseFloat(newValue);
        if (parsedValue > maxValue) {
          return maxValue;
        } else return parsedValue;
      },
    },
    {
      field: "totalItem",
      headerName: "Total Ítem (R$)",
      flex: 300,
      valueGetter: (params) => {
        const total = params.row.valorUnitario * params.row.saldoExportar;
        return formatPrice(total);
      },
    },
  ];

  function filtrarCondicionaisSelecionados(condicionaisIds, activeStep) {
    let itensImportacao = [];
    const selected = condicionais.filter(
      (condicional) => condicionaisIds.indexOf(condicional.id) > -1,
    );
    if (!selected.length) return;
    const clienteIds = [
      ...new Set(selected.map((o) => o.pessoaClienteId)),
    ].filter(Boolean);
    if (clienteIds.length > 1) {
      notification.alertaGenericos("Condicionais devem ser do mesmo cliente");
      return activeStep - 1;
    }
    let condicionalImportacao = null;
    const invalidKeys = ["id", "itens"];
    const condicionaisModificados = selected.map((condicional) => {
      if (condicionalImportacao === null) {
        condicionalImportacao = { ...condicional };
      }
      for (const key in condicional) {
        if (
          condicionalImportacao[key] != condicional[key] &&
          !invalidKeys.includes(key)
        ) {
          condicional[key] = null;
        }
      }
      itensImportacao.push(...(condicional.itens || []));
      return condicional;
    });
    setCondicionaisSelecionados(condicionaisModificados);
    buscarRelacionamentoProdutos(itensImportacao);
    return activeStep;
  }

  const handleAvancarStep = () => {
    if (activeStep === 0) {
      if (!condicionaisSelectionModel?.length)
        return notification.alertaGenericos(
          "Selecione ao menos uma condicional para importar",
        );
      const nextStep = filtrarCondicionaisSelecionados(
        condicionaisSelectionModel,
        activeStep,
      );
      setActiveStep(nextStep + 1);
      return;
    }

    if (activeStep === 1) {
      if (!itemSelectionModel?.length)
        return notification.alertaGenericos(
          "Selecione ao menos um produto para importar",
        );
    }
    setActiveStep(activeStep + 1);
  };

  const handleVoltarStep = () => {
    setActiveStep(activeStep - 1);
  };

  const buscarRelacionamentoProdutos = async (listaItens) => {
    setLoading(true);
    const idsProdutos = listaItens.map((item) => item.produtoId);
    const filtros = {
      id: idsProdutos.length > 0 ? idsProdutos : undefined,
    };
    const res = await produtosService.getAllProdutosSimples(filtros);
    if (!res.isAxiosError) {
      if (res.data.rows.length) {
        const itensSelecionadosTemp = listaItens
          .filter((produto) => produto.saldoExportar > 0)
          .map((produto) => {
            const produtoEncontrado = res.data.rows.find(
              (item) => item.id == produto.produtoId,
            );
            return {
              ...produto,
              ...produtoEncontrado,
              id: shortid.generate(),
              produto: produtoEncontrado?.descricao ?? "",
            };
          });
        setItemSelectionModel(itensSelecionadosTemp.map(({ id }) => id));
        setItensQuantidade(itensSelecionadosTemp);
        setItensSelecionados(itensSelecionadosTemp);
      }
    }
    setLoading(false);
  };

  const onChangeAutocomplete = (name, value) => {
    setFiltrosDialog({
      ...filtrosDialog,
      [name]: value ? value.id : undefined,
    });
  };

  const onCellKeyDown = (params, event) => {
    if (event.key === "Enter") {
      const value = Number(event.target.value);
      const itensTemp = itensSelecionados.map((item) => {
        if (params.row.id == item.id) {
          if (params.field == "saldoExportar")
            return {
              ...item,
              [params.field]: value ?? null,
              quantidade: value,
            };
        }
        return item;
      });
      setItensSelecionados(itensTemp);
    }
  };

  switch (activeStep) {
    case 2:
      return (
        <>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  id="operacaoFiscalId"
                  name="operacaoFiscalId"
                  required
                  options={listaOperacoesFiscais}
                  noOptionsText="Sem opções"
                  autoHighlight
                  getOptionLabel={(option) =>
                    option.descricao ? `${option.descricao}` : ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operação Fiscal"
                      variant="outlined"
                      margin="normal"
                      error={inputErros.get("operacaoFiscalId")}
                      required
                    />
                  )}
                  onChange={(e, value) => {
                    setOperacaoFiscal(value);
                  }}
                  value={value.autoComplete(
                    listaOperacoesFiscais,
                    operacaoFiscal?.id,
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className="m-3">
            <Button
              onClick={handleCancelarImportacao}
              color="padrao"
              variant="contained"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleVoltarStep}
              color="primary"
              variant="contained"
            >
              <i
                className="ph-fill ph-caret-left"
                style={{ color: "#fff", fontSize: 18 }}
              ></i>
              Anterior
            </Button>
            <Button
              onClick={handleImportarCondicional}
              color="primary"
              variant="contained"
            >
              Importar
            </Button>
          </DialogActions>
        </>
      );
    case 1:
      return (
        <>
          <DialogContent>
            <DataGrid
              rows={itensSelecionados}
              columns={colunasItens}
              rowCount={itensSelecionados.length}
              checkboxSelection
              selectionModel={itemSelectionModel}
              onRowSelectionModelChange={(selectedIds) => {
                setItemSelectionModel(selectedIds);
              }}
              onCellKeyDown={onCellKeyDown}
              disableAcoes
            />
          </DialogContent>
          <DialogActions className="m-3">
            <Button
              onClick={handleCancelarImportacao}
              color="padrao"
              variant="contained"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleVoltarStep}
              color="primary"
              variant="contained"
            >
              <i
                className="ph-fill ph-caret-left"
                style={{ color: "#fff", fontSize: 18 }}
              ></i>
              Anterior
            </Button>
            <Button
              onClick={() => handleAvancarStep()}
              color="primary"
              variant="contained"
            >
              Próximo
              <i
                className="ph-fill ph-caret-right"
                style={{ color: "#fff", fontSize: 18 }}
              ></i>
            </Button>
          </DialogActions>
          <Loading loading={loading} />
        </>
      );
    default:
      return (
        <>
          <DialogContent>
            <Grid container spacing={2} className="mt-1 mb-4">
              <Grid item xs={2}>
                <DatePicker
                  id="dataInicial"
                  name="dataInicial"
                  label="Data Inicial"
                  format="dd/MM/yyyy"
                  value={
                    filtrosDialog?.dataInicial
                      ? filtrosDialog.dataInicial
                      : null
                  }
                  onChange={(date) => handleDateChange(date, "dataInicial")}
                />
              </Grid>
              <Grid item xs={2}>
                <DatePicker
                  id="dataFinal"
                  name="dataFinal"
                  label="Data Final"
                  format="dd/MM/yyyy"
                  value={
                    filtrosDialog?.dataFinal ? filtrosDialog.dataFinal : null
                  }
                  onChange={(date) => handleDateChange(date, "dataFinal")}
                />
              </Grid>
              <Grid item xs={3}>
                <Autocomplete
                  id="pessoaClienteId"
                  name="pessoaClienteId"
                  options={listaClientes}
                  autoHighlight
                  getOptionLabel={(option) =>
                    option.nomeRazaoSocial
                      ? `${option.id}` + ` - ` + `${option.nomeRazaoSocial}`
                      : ""
                  }
                  getOptionSelected={(option, value) =>
                    option?.nomeRazaoSocial === value?.nomeRazaoSocial
                  }
                  noOptionsText="Sem opções"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      variant={"outlined"}
                      error={inputErros.get("pessoaClienteId")}
                    />
                  )}
                  onChange={(e, value) =>
                    onChangeAutocomplete("pessoaClienteId", value)
                  }
                  value={value.autoComplete(
                    listaClientes,
                    filtrosDialog?.pessoaClienteId,
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  id="numeroDocumento"
                  name="numeroDocumento"
                  label="Nº do documento"
                  variant="outlined"
                  value={
                    filtrosDialog.numeroDocumento
                      ? filtrosDialog.numeroDocumento
                      : undefined
                  }
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={2} className="d-flex align-items-center">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={{ height: "51px" }}
                  onClick={() => buscarCondicionaisAvancado(filtrosDialog)}
                >
                  <i
                    className="ph-bold ph-magnifying-glass"
                    style={{ color: "#fff", fontSize: 18, marginRight: 10 }}
                  ></i>
                  Filtrar
                </Button>
              </Grid>
            </Grid>
            <DataGrid
              rows={condicionais}
              columns={colunas}
              checkboxSelection
              selectionModel={condicionaisSelectionModel}
              onRowSelectionModelChange={(selectedIds) => {
                setCondicionaisSelectionModel(selectedIds);
              }}
              paginationMode={"server"}
              sendServer={sendServer}
              disableAcoes
              noFilter
            />
          </DialogContent>
          <DialogActions className="m-3">
            <Button
              onClick={handleCancelarImportacao}
              color="padrao"
              variant="contained"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleAvancarStep()}
              color="primary"
              variant="contained"
            >
              Próximo
              <i
                className="ph-fill ph-caret-right"
                style={{ color: "#fff", fontSize: 18 }}
              ></i>
            </Button>
          </DialogActions>
          <Loading loading={loading} />
        </>
      );
  }
};

export default CondicionalStepperContent;
